import React, { createContext, useEffect } from 'react';
import { useSetState, useInterval } from '@mantine/hooks';
import { v4 as uuidv4 } from 'uuid';

const DataContext = createContext();

export function ProvideData(v) {
  const auth = useProvideData(v.platformContext);
  return <DataContext.Provider value={auth}>{v.children}</DataContext.Provider>;
}

export default DataContext;

// Per-tab identifier used only by the backend's onlineusers ZSET for the
// "how many people are watching" count. Never doubles as gameSessionId.
function getBrowserSessionId() {
  try {
    let id = sessionStorage.getItem('browserSessionId');
    if (!id) {
      id = uuidv4();
      sessionStorage.setItem('browserSessionId', id);
    }
    return id;
  } catch (err) {
    return uuidv4();
  }
}

export function useProvideData() {
  const getInitialState = () => {
    try {
      return {
        appLaunchedAt:
          parseInt(localStorage.getItem('appLaunchedAt'), 10) ||
          new Date().getTime(),
        playerName: localStorage.getItem('playerName') || '',
        results: JSON.parse(localStorage.getItem('results')) || null,
        sessionCount: parseInt(localStorage.getItem('sessionCount'), 10) || 0,
        sortColumn: localStorage.getItem('sortColumn') || 'score',
      };
    } catch (error) {
      console.error(
        'Error retrieving initial state from local storage:',
        error,
      );
      return {
        appLaunchedAt: new Date().getTime(),
        playerName: '',
        results: null,
        sessionCount: 0,
        sortColumn: 'score',
      };
    }
  };

  const [dataState, setDataState] = useSetState(getInitialState());
  const resultsInterval = useInterval(() => getResults(), 5000);

  useEffect(() => {
    const initialAppLaunchedAt =
      parseInt(localStorage.getItem('appLaunchedAt'), 10) ||
      new Date().getTime();
    if (!localStorage.getItem('appLaunchedAt')) {
      localStorage.setItem('appLaunchedAt', initialAppLaunchedAt.toString());
    }
    setDataState({ appLaunchedAt: initialAppLaunchedAt });
    getResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resultsInterval.start();
    return resultsInterval.stop;
  }, [dataState?.sortColumn, resultsInterval]);

  // Background leaderboard fetch. Swallows errors so transient backend outages
  // don't surface as unhandled promise rejections in the CRA error overlay.
  const getResults = async () => {
    try {
      const playerName = localStorage.getItem('playerName') || '';
      const browserSessionId = getBrowserSessionId();
      const host = window.location.hostname.includes(
        'relibirds.newrelic-es.com',
      )
        ? 'api.relibirds.newrelic-es.com'
        : `${window.location.hostname}:3333`;

      const url = `${window.location.protocol}//${host}/leaderboard?id=${browserSessionId}&playerName=${playerName}&sortColumn=${dataState.sortColumn}`;
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`[getResults] HTTP ${response.status}`);
        return null;
      }

      const json = await response.json();
      setDataState({
        sessionCount: json?.sessionCount ?? 0,
        results: json?.results ?? null,
      });
      return json?.results;
    } catch (error) {
      console.warn('[getResults] fetch failed:', error?.message || error);
      return null;
    }
  };

  return {
    ...dataState,
    setDataState,
  };
}
